import React, { forwardRef } from 'react'
import { Platform, type StyleProp, Text as RnText, type TextStyle } from 'react-native'
import { twMerge } from 'tailwind-merge'

import { Text as GsText } from '../../_gluestack/text'
import { type TextProps } from './Text.types'
import { textVariants } from './variants'

type TextRef = React.ComponentRef<typeof RnText>

// `unknown` param avoids StyleProp's recursive-array type not structurally
// matching its own element type once Array.isArray narrows it.
const flattenStyle = (style: unknown): TextStyle =>
    Array.isArray(style)
        ? Object.assign({}, ...style.map(flattenStyle))
        : ((style || {}) as TextStyle)

export const Text = forwardRef<TextRef, TextProps>(
    ({ className, size, weight, tone, color, selectable, children, ...rest }, ref) => {
        const variantClasses = textVariants({ size, weight, tone })
        // `style` is off TextProps, but a JSX spread still carries one through at
        // runtime; keep it as the base of the array so it cannot replace what this
        // component owns below.
        const { style: callerStyle, ...textProps } = rest as typeof rest & {
            style?: StyleProp<TextStyle>
        }
        return (
            <GsText
                ref={ref}
                className={twMerge(variantClasses, selectable && 'web:select-text', className)}
                // includeFontPadding restores legacy parity (aos-ui-common Font.ts
                // set it app-wide): Android otherwise pads the first line's ascent,
                // shifting glyphs down inside the line box so a compact leading
                // (e.g. the dashboard title band's leading-4) clips descenders that
                // iOS renders fine (AOS-1201).
                // Flattened to a single object: the web GsText spreads `style`
                // into a plain object, so a style array would arrive as indexed
                // properties and crash CSSStyleDeclaration.
                style={{
                    ...flattenStyle(callerStyle),
                    ...(Platform.OS === 'android' && { includeFontPadding: false }),
                    ...(color !== undefined && { color }),
                }}
                {...textProps}
                {...(Platform.OS !== 'web' && { selectable })}
            >
                {children}
            </GsText>
        )
    },
)

Text.displayName = 'Text'
